<template>
  <div class="p-4 bg-white rounded-lg">
    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-semibold">
        Follow-up
        <span
            v-if="schedule && schedule.job_id"
            class="text-base font-normal text-gray-500 ml-3"
        >
          (Job ID: {{ schedule.job_id }})
        </span>
      </h2>

      <div class="flex items-center space-x-3">
        <!-- Status chip -->
        <span
            v-if="statusLabel"
            class="px-2 py-1 rounded-full text-xs font-semibold"
            :class="statusClass"
        >
          {{ statusLabel }}
        </span>

        <!-- Scheduled job link (when follow-up is scheduled) -->
        <div
            v-if="status === 'scheduled' && scheduledJobId"
            class="text-xs"
        >
          <span class="text-gray-500 mr-1">Scheduled to:</span>
          <button
              type="button"
              class="text-blue-600 underline hover:text-blue-800"
              @click="goToScheduledJob"
          >
            {{ scheduledJobId }}
          </button>
        </div>
      </div>
    </div>

    <!-- Equipment summary -->
    <div v-if="equipment" class="mb-4">
      <p class="text-sm font-semibold">
        Equipment: {{ safeDisplay(equipment.name) }}
      </p>
      <p class="text-xs text-gray-500">
        {{ safeDisplay(equipment.product_code) }} •
        {{ safeDisplay(equipment.serial_number) }}
      </p>
    </div>

    <!-- Reason -->
    <div class="mb-4">
      <label class="uppercase font-semibold text-xs mb-1 block">Reason</label>
      <input
          v-model="form.reason"
          type="text"
          class="w-full bge-input bge-input-spacing rounded"
          placeholder="e.g. No access, Parts required, Customer postponed"
      />
    </div>

    <!-- Notes -->
    <div class="mb-4">
      <label class="uppercase font-semibold text-xs mb-1 block">Notes</label>
      <textarea
          v-model="form.note"
          rows="3"
          class="w-full bge-input bge-input-spacing rounded"
          placeholder="Optional extra details"
      ></textarea>
    </div>

    <!-- Actions -->
    <div class="flex justify-end space-x-2 pt-2">
      <button
          type="button"
          class="px-4 py-2 text-sm rounded bg-gray-200 text-gray-700"
          @click="$emit('close')"
      >
        Cancel
      </button>
      <button
          type="button"
          class="px-4 py-2 text-sm rounded bg-blue-500 text-white"
          @click="saveFollowUp"
          :disabled="isBusy"
      >
        {{ followUpId ? 'Update Follow-up' : 'Save Follow-up' }}
      </button>
    </div>
  </div>
</template>

<script>
import notificationMixin from '@/mixins/notificationMixin';

export default {
  name: 'ScheduleFollowUpPanel',
  mixins: [notificationMixin],
  props: {
    schedule: { type: Object, required: true },   // full schedule object
    equipment: { type: Object, required: true },  // equipment row (followUpTargetItem)
    followUpId: { type: String, default: null },  // null = create, value = edit
  },
  data() {
    return {
      isBusy: false,
      form: {
        reason: '',
        note: '',
      },
      status: null,         // 'new' | 'open' | 'scheduled' | 'done' | null
      scheduledJobId: null, // job id of scheduled follow-up, if any
    };
  },
  computed: {
    statusLabel() {
      if (!this.status) return null;
      switch (this.status) {
        case 'new':
          return 'New';
        case 'open':
          return 'Open';
        case 'scheduled':
          return 'Scheduled';
        case 'done':
          return 'Done';
        default:
          return this.status;
      }
    },
    statusClass() {
      switch (this.status) {
        case 'new':
          return 'bg-gray-100 text-gray-700';
        case 'open':
          return 'bg-yellow-100 text-yellow-800';
        case 'scheduled':
          return 'bg-blue-100 text-blue-800';
        case 'done':
          return 'bg-green-100 text-green-800';
        default:
          return 'bg-gray-200 text-gray-700';
      }
    },
  },
  watch: {
    followUpId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadFollowUp(newVal);
        } else {
          this.initCreateForm();
        }
      },
    },
  },
  methods: {
    safeDisplay(val, fallback = 'No data') {
      return val && val !== '' ? val : fallback;
    },

    initCreateForm() {
      this.form = {
        reason: '',
        note: '',
      };
      this.status = 'new';
      this.scheduledJobId = null;
    },

    async loadFollowUp(id) {
      this.isBusy = true;
      try {
        const data = await this.ScheduleFollowUpService.getFollowUpById(id);
        this.form.reason = data.reason || '';
        this.form.note = data.note || '';
        this.status = data.status || 'open';

        // Try to resolve scheduled job id from populated schedule or flat field
        this.scheduledJobId =
            (data.scheduled_schedule && data.scheduled_schedule.job_id) ||
            data.scheduled_job_id ||
            null;
      } catch (err) {
        console.error(err);
        this.notifyError('Failed to load follow-up');
        this.initCreateForm();
      } finally {
        this.isBusy = false;
      }
    },

    goToScheduledJob() {
      if (!this.scheduledJobId) return;

      this.$emit('close');

      this.$nextTick(() => {
        this.$router.push({
          name: 'my-schedule-job-details',
          params: { job_id: this.scheduledJobId },
        });
      });
    },


    async saveFollowUp() {
      if (!this.form.reason || !this.form.reason.trim()) {
        this.notifyError('Please enter a reason for the follow-up');
        return;
      }

      const payload = {
        reason: this.form.reason.trim(),
        note: (this.form.note || '').trim(),
        // status handled by backend
      };

      this.isBusy = true;
      try {
        if (this.followUpId) {
          // edit existing (only reason + note)
          await this.ScheduleFollowUpService.updateFollowUp(this.followUpId, payload);
          this.notifySuccess('Follow-up updated successfully');
        } else {
          // create new – backend will set initial status (e.g. 'open')
          const createPayload = {
            ...payload,
            equipment_id: this.equipment.equipment_id,
            customer_id:
                (this.schedule.customer && this.schedule.customer.contact_id) ||
                this.schedule.customer_id,
            site_id:
                this.schedule.customer &&
                this.schedule.customer.default_address &&
                this.schedule.customer.default_address.site_id
                    ? this.schedule.customer.default_address.site_id
                    : null,
            origin_schedule_id: this.schedule.schedule_id,
            origin_item_id: this.equipment.equipment_id,
          };
          await this.ScheduleFollowUpService.createFollowUp(createPayload);
          this.notifySuccess('Follow-up created successfully');
        }

        this.$emit('saved'); // let parent refresh if needed
        this.$emit('close');
      } catch (err) {
        console.error(err);
        this.notifyError('Failed to save follow-up');
      } finally {
        this.isBusy = false;
      }
    },
  },
};
</script>

<style scoped>
</style>
